import {
    API,
    EditCandidacyModalButton,
    resolveSortField,
    RowsPerPageSelect,
    usePaginatedSort,
    Utils,
} from '@enrich/shared-lib';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Text,
} from 'grommet';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useLoaderData, useParams } from 'react-router-dom';
import { BulkMessageButton } from '../components/BulkMessageButton';

export const Candidates = () => {
    const { candidacyStatusEnums } = useLoaderData();
    const clientCandidacyStatusEnums =
        Utils.filterClientCandidacyStatuses(candidacyStatusEnums);
    const { companySlug } = useParams();
    const [candidates, setCandidates] = useState([]);
    // Bumped after a candidacy save to re-run the fetch effect. The list is
    // no longer loader data, so useRevalidator() would not refresh it.
    const [refreshKey, setRefreshKey] = useState(0);
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'lastName', direction: 'asc' },
    });

    useEffect(() => {
        let isActive = true;

        (async () => {
            const response = await loadCandidates({
                companySlug,
                page,
                pageSize,
                sort,
            });
            if (!isActive) return;
            setCandidates(response.data);
            applyPagination(response.meta);
        })();

        return () => {
            isActive = false;
        };
    }, [companySlug, page, pageSize, sort, refreshKey, applyPagination]);

    const dataColumns = [
        {
            header: 'Name',
            property: 'lastName',
            render: (contact) => (
                <Anchor
                    key={contact.id}
                    href={`/${companySlug}/candidates/${contact.id}`}
                    label={`${contact.firstName} ${contact.lastName}`}
                />
            ),
            sortable: true,
        },
        {
            header: 'Job',
            property: 'candidacies',
            render: (contact) => {
                const candidacies = contact.candidacies || [];
                if (candidacies.length === 0) return <Text>N/A</Text>;
                return (
                    <Box gap="xsmall">
                        {candidacies.map((candidacy) => (
                            <Text key={candidacy.id}>
                                {candidacy.job?.title || 'N/A'}
                            </Text>
                        ))}
                    </Box>
                );
            },
        },
        {
            header: 'Status',
            property: 'candidacies',
            render: (contact) => {
                const candidacies = contact.candidacies || [];
                if (candidacies.length === 0) return <Text>N/A</Text>;
                return (
                    <Box gap="xsmall">
                        {candidacies.map((candidacy) => (
                            <Box
                                key={candidacy.id}
                                direction="row"
                                gap="small"
                                align="center">
                                <Text>{candidacy.status}</Text>
                                {clientCandidacyStatusEnums.length > 0 && (
                                    <EditCandidacyModalButton
                                        candidacy={candidacy}
                                        candidacyStatusEnums={
                                            clientCandidacyStatusEnums
                                        }
                                        onSaved={() =>
                                            setRefreshKey((key) => key + 1)
                                        }
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                );
            },
        },
        {
            header: 'Graduation',
            property: 'graduationYear',
            render: (contact) => {
                const semester = contact.graduationSemester;
                const formattedSemester = semester
                    ? semester[0].toUpperCase() + semester.slice(1)
                    : '';
                return `${formattedSemester} ${contact.graduationYear || ''}`;
            },
            sortable: true,
        },
        {
            header: 'School',
            property: 'school.name',
            render: (contact) => contact.school?.name || 'N/A',
        },
        {
            header: 'Hometown',
            property: 'hometown',
            sortable: true,
        },
        {
            header: 'Majors',
            property: 'majors',
            render: (contact) => {
                const majors = contact.majors?.map((major) => major.name);
                return majors?.length > 0 ? majors.join(', ') : 'N/A';
            },
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box
                    direction="row-responsive"
                    justify="between"
                    align="center">
                    <Heading level="2">Candidates</Heading>
                    <BulkMessageButton
                        candidacyStatusEnums={clientCandidacyStatusEnums}
                        companySlug={companySlug}
                    />
                </Box>
                <Box>
                    {candidates.length === 0 ? (
                        <Box pad="medium" align="center">
                            <Text>No candidates found</Text>
                        </Box>
                    ) : (
                        <DataTable
                            primaryKey="id"
                            sort={{ ...sort, external: true }}
                            onSort={handleSort}
                            border="bottom"
                            data={candidates}
                            columns={dataColumns}
                        />
                    )}
                </Box>
                <Box
                    direction="row-responsive"
                    align="center"
                    justify="between"
                    gap="small"
                    pad="small">
                    <RowsPerPageSelect
                        options={stepOptions}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    />
                    <Pagination
                        numberItems={totalItems}
                        page={page}
                        onChange={handlePageChange}
                        step={pageSize}
                        summary={true}
                    />
                </Box>
            </PageContent>
        </Page>
    );
};

// DataTable column property -> backend sort field. The CMS allow-lists these
// (ENG-355); anything unmapped falls back to lastName.
const SORT_FIELD_MAP = {
    lastName: 'lastName',
    graduationYear: 'graduationYear',
    hometown: 'hometown',
};

const loadCandidates = async ({ companySlug, page, pageSize, sort } = {}) => {
    const sortField = resolveSortField(sort, SORT_FIELD_MAP, 'lastName');
    const sortDir = sort?.direction ?? 'asc';
    const query = QueryString.stringify(
        {
            companySlug,
            page,
            pageSize,
            sort: `${sortField}:${sortDir}`,
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/company-candidates?${query}`);
        return { data: response.data.data, meta: response.data.meta };
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0, page } } };
};

// Only the status enums are loaded up front — they do not change per page,
// unlike the candidate list, which the component fetches per page/sort.
export async function loader() {
    const graphqlQuery = `query myQuery {
        getFilterFields {
            candidacyStatuses {
                id
                name
            }
        }
    }`;

    try {
        const response = await API.post('graphql', { query: graphqlQuery });
        return {
            candidacyStatusEnums:
                response.data.data?.getFilterFields?.candidacyStatuses ?? [],
        };
    } catch (error) {
        console.log(error);
    }

    return { candidacyStatusEnums: [] };
}
